import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Settings2, Plus } from "lucide-react";
import { CostCentresTable, DEFAULT_COST_CENTRE_COLUMNS } from "@/components/blocks/cost-centres/cost-centres-table";
import type { CostCentreColumnDefinition } from "@/components/blocks/cost-centres/cost-centres-table";
import { CostCentresHierarchicalTable } from "@/components/blocks/cost-centres/cost-centres-hierarchical-table";
import { useProjectDetailsContext } from "../context";
import { useBudgetPermissions } from "@/hooks/use-budget-permissions";

// Keys that are only meaningful when budget values exist (Cost Centre mode)
export const BUDGET_ONLY_COLUMN_KEYS: string[] = [
  'costsBudget',
  'costVariance',
  'costPercent',
  'remainingCosts',
  'forecastCosts',
  'revenueBudget',
  'revenueVariance',
  'revenuePercent',
  'remainingRevenue',
  'forecastRevenue',
  'hoursBudget',
  'hoursVariance',
  'hoursPercent',
  'remainingHours',
];

function buildColumns(isCostCentreMode: boolean): CostCentreColumnDefinition[] {
  return DEFAULT_COST_CENTRE_COLUMNS.map((col) => {
    const isBudgetOnlyCol = BUDGET_ONLY_COLUMN_KEYS.includes(col.key as string);
    if (isBudgetOnlyCol) {
      // Budget/variance/% columns only visible in Cost Centre mode
      return { ...col, visible: isCostCentreMode && col.visible };
    }
    // Actuals + identification columns — keep their default visibility
    return { ...col };
  });
}

export function CostCentresTab() {
  const ctx = useProjectDetailsContext();
  const {
    costCentres,
    costCentresLoading,
    hierarchicalCostCentres,
    costCentreColumns,
    setCostCentreColumnConfigOpen,
    isStructureView,
    setIsStructureView,
    setSelectedCostCentre,
    setCostCentreDetailsPanelOpen,
    setCreateCostCentrePanelOpen,
    setConfigureStructureOpen,
  } = ctx;

  const { isCostCentreMode } = useBudgetPermissions();

  // Build mode-aware column set — override visibility based on budget mode.
  // If the user has customised columns via costCentreColumns (from context), respect that
  // but still enforce budget-column visibility rules.
  const modeAwareColumns = useMemo<CostCentreColumnDefinition[]>(() => {
    const base = costCentreColumns.length > 0 ? costCentreColumns : buildColumns(isCostCentreMode);
    return base.map((col) => {
      const isBudgetOnlyCol = BUDGET_ONLY_COLUMN_KEYS.includes(col.key as string);
      if (isBudgetOnlyCol && !isCostCentreMode) {
        // Force-hide budget-only columns when not in Cost Centre mode
        return { ...col, visible: false };
      }
      return col;
    });
  }, [costCentreColumns, isCostCentreMode]);

  return (
    <>
      {/* Header Section */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCostCentreColumnConfigOpen(true)}
            className="gap-2"
          >
            <Settings2 className="h-4 w-4" />
            Configure Columns
          </Button>
          {!isStructureView && (
            <Button size="sm" onClick={() => setCreateCostCentrePanelOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              New
            </Button>
          )}
          {isStructureView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfigureStructureOpen(true)}
              className="gap-2"
            >
              <Settings2 className="h-4 w-4" />
              Configure Structure
            </Button>
          )}
          <div className="flex items-center gap-2">
            <label htmlFor="structure-view-toggle" className="text-sm font-medium cursor-pointer">
              Structure View
            </label>
            <Switch
              id="structure-view-toggle"
              checked={isStructureView}
              onCheckedChange={setIsStructureView}
            />
          </div>
        </div>
      </div>

      {/* Content Display Area */}
      {isStructureView ? (
        <CostCentresHierarchicalTable
          data={hierarchicalCostCentres}
          columns={modeAwareColumns}
          loading={costCentresLoading}
          useCardWrapper={false}
          onRowClick={(costCentre) => {
            setSelectedCostCentre(costCentre);
            setCostCentreDetailsPanelOpen(true);
          }}
        />
      ) : (
        <CostCentresTable
          data={costCentres}
          columns={modeAwareColumns}
          loading={costCentresLoading}
          useCardWrapper={false}
          onRowClick={(costCentre) => {
            setSelectedCostCentre(costCentre);
            setCostCentreDetailsPanelOpen(true);
          }}
        />
      )}
    </>
  );
}
